import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IBusiness, IInvoice, IInvoiceLineItem } from '../../../models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DynamicHeight } from '../../../helper';
import { CommonService, SnackbarService } from '../../../services';
import { BusinessService } from '../../../services/business';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { Constants } from '../../../common';

@Component({
  selector: 'app-generate-invoice',
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatTableModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    DynamicHeight,
  ],
  providers: [DatePipe],
  templateUrl: './generate-invoice.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './generate-invoice.component.scss',
})
export class GenerateInvoiceComponent implements OnInit {

  public is_edit = false;

  generateInvoiceForm!: FormGroup;
  displayedColumns = ['item', 'hsn_number', 'quantity', 'rate', 'total_amount', 'actions'];
  footerColumns = [...this.displayedColumns];

  currentInvoice: IInvoice = {} as IInvoice;

  @ViewChild(MatTable) table!: MatTable<IInvoiceLineItem>;

  constructor(private fb: FormBuilder,
    public dialogRef: MatDialogRef<GenerateInvoiceComponent>, private snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public dialog_data: { business: IBusiness, invoice?: IInvoice, is_edit: boolean },
    public commonService: CommonService, private businessService: BusinessService, private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    this.currentInvoice = this.dialog_data?.invoice ? this.dialog_data.invoice : {} as IInvoice;
    this.currentInvoice.tax_1_name = this.currentInvoice.tax_1_name ? this.currentInvoice.tax_1_name : 'SGST';
    this.currentInvoice.tax_2_name = this.currentInvoice.tax_2_name ? this.currentInvoice.tax_2_name : 'CGST';
    const date = this.datePipe.transform(new Date(), Constants.DATE_FORMAT_FOR_PYTHON);
    this.generateInvoiceForm = this.fb.group({
      id: [this.currentInvoice.id ? this.currentInvoice.id : 0],
      invoice_number: [this.currentInvoice?.invoice_number ? this.currentInvoice.invoice_number : '#XXXX-XXXXXXXXXXXX'],
      invoice_date: [this.currentInvoice?.invoice_date ? this.currentInvoice.invoice_date : date],
      start_date: [this.currentInvoice?.start_date ? this.currentInvoice.start_date : null],
      end_date: [this.currentInvoice?.end_date ? this.currentInvoice.end_date : null],
      invoice_time: [],
      tax_1: [this.currentInvoice?.tax_1 ?? 0, [Validators.min(0), Validators.max(100)]],
      tax_2: [this.currentInvoice?.tax_2 ?? 0, [Validators.min(0), Validators.max(100)]],
      tax_1_name:[this.currentInvoice?.tax_1_name ? this.currentInvoice.tax_1_name : 'SGST'],
      tax_2_name:[this.currentInvoice?.tax_2_name ? this.currentInvoice.tax_2_name : 'CGST'],
      discount_amount: [this.currentInvoice?.discount_amount ? this.currentInvoice.discount_amount : 0],
      sub_total: [{ value: this.currentInvoice?.sub_total || 0, disabled: true }],
      grand_total: [{ value: this.currentInvoice?.grand_total || 0, disabled: true }],
      is_payment_done: [this.currentInvoice?.is_payment_done ? true : false],
      is_hide_payment_details: [this.currentInvoice?.is_hide_payment_details ?? false],
      created_at: [this.currentInvoice?.created_at ? this.currentInvoice.created_at : date],
      updated_at: [this.currentInvoice?.updated_at ? this.currentInvoice.updated_at : date],
      business: [this.currentInvoice.business?this.currentInvoice.business: this.dialog_data.business.business],
      invoice_line_item: this.fb.array(
        this.currentInvoice?.invoice_line_item?.length > 0
          ? this.currentInvoice.invoice_line_item.map(item => this.createInvoiceLineItem(item))
          : []
      ),
    }, { validators: [this.taxPercentSumValidator(), this.dateRangeValidator()] });

    // Calculate total_amount for each line item explicitly
    this.invoiceLineItems.controls.forEach((_, i) => this.updateTotal(i));

    // Update grand total after line items totals are set
    this.updateGrandTotal();

    this.generateInvoiceForm.get('discount_amount')?.valueChanges.subscribe(() => this.updateGrandTotal());
    this.generateInvoiceForm.get('tax_1')?.valueChanges.subscribe(() => this.updateGrandTotal());
    this.generateInvoiceForm.get('tax_2')?.valueChanges.subscribe(() => this.updateGrandTotal());
  }

  taxPercentSumValidator() {
    return (form: FormGroup) => {
      const tax1Control = form.get('tax_1');
      const tax2Control = form.get('tax_2');

      const t1 = +tax1Control?.value || 0;
      const t2 = +tax2Control?.value || 0;

      if (t1 + t2 > 100) {
        // Set custom error on both controls
        tax1Control?.setErrors({ taxPercentSumInvalid: true });
        tax2Control?.setErrors({ taxPercentSumInvalid: true });
      } else {
        // Clear the error on both controls if it exists
        if (tax1Control?.hasError('taxPercentSumInvalid')) {
          const errors = { ...tax1Control.errors };
          delete errors['taxPercentSumInvalid'];
          tax1Control.setErrors(Object.keys(errors).length ? errors : null);
        }
        if (tax2Control?.hasError('taxPercentSumInvalid')) {
          const errors = { ...tax2Control.errors };
          delete errors['taxPercentSumInvalid'];
          tax2Control.setErrors(Object.keys(errors).length ? errors : null);
        }
      }

      // Return null since we're managing errors directly on controls
      return null;
    };
  }

  dateRangeValidator() {
    return (form: FormGroup) => {
      const startCtrl = form.get('start_date');
      const endCtrl = form.get('end_date');

      const start = startCtrl?.value;
      const end = endCtrl?.value;

      const clearError = (ctrl: any, key: string) => {
        if (!ctrl?.errors) return;
        if (!ctrl.hasError(key)) return;

        const errors = { ...ctrl.errors };
        delete errors[key];
        ctrl.setErrors(Object.keys(errors).length ? errors : null);
      };

      // 1) If only one date is filled -> invalid (both should be filled or both empty)
      if ((start && !end) || (!start && end)) {
        startCtrl?.setErrors({ ...(startCtrl.errors || {}), datePairInvalid: true });
        endCtrl?.setErrors({ ...(endCtrl.errors || {}), datePairInvalid: true });
      } else {
        // Valid pair (both empty OR both filled) -> clear datePairInvalid
        clearError(startCtrl, 'datePairInvalid');
        clearError(endCtrl, 'datePairInvalid');
      }

      // 2) If both filled, check the range
      if (start && end && new Date(start) > new Date(end)) {
        startCtrl?.setErrors({ ...(startCtrl.errors || {}), dateRangeInvalid: true });
        endCtrl?.setErrors({ ...(endCtrl.errors || {}), dateRangeInvalid: true });
      } else {
        // Clear range error if valid or not applicable
        clearError(startCtrl, 'dateRangeInvalid');
        clearError(endCtrl, 'dateRangeInvalid');
      }

      return null;
    };
  }



  get invoiceLineItems(): FormArray {
    return this.generateInvoiceForm.get('invoice_line_item') as FormArray;
  }

  createInvoiceLineItem(invoice_line_item?: IInvoiceLineItem): FormGroup {
    return this.fb.group({
      id: [invoice_line_item?.id ?? null],
      item: [invoice_line_item?.item ?? '', Validators.required],
      hsn_number: [invoice_line_item?.hsn_number ?? ''],
      quantity: [
        invoice_line_item?.quantity ?? 1,
        [Validators.required, Validators.min(0)],
      ],
      rate: [
        invoice_line_item?.rate ?? 0,
        [Validators.required, Validators.min(0)],
      ],
      total_amount: [{ value: invoice_line_item?.total_amount ?? 0, disabled: true }],
    });
  }

  addParticular(): void {
    this.invoiceLineItems.push(this.createInvoiceLineItem());
    this.table.renderRows();
  }

  removeParticular(index: number): void {
    this.invoiceLineItems.removeAt(index);
    this.table.renderRows();
  }

  updateTotal(index: number): void {
    const particular = this.invoiceLineItems.at(index);
    if (!particular) return;

    const rateRaw = +particular.get('rate')?.value || 0;
    const quantityRaw = +particular.get('quantity')?.value || 0;

    // Round rate and quantity input (optional - depending if you want to enforce decimals here)
    const rate = +rateRaw.toFixed(2);
    const quantity = +quantityRaw.toFixed(2);

    // Update form controls with rounded values to keep consistency
    particular.get('rate')?.setValue(rate, { emitEvent: false });
    particular.get('quantity')?.setValue(quantity, { emitEvent: false });

    const total = +(rate * quantity).toFixed(2);
    particular.get('total_amount')?.setValue(total, { emitEvent: false });
    this.updateGrandTotal();
  }

  updateGrandTotal(): void {
    const lineItems = this.invoiceLineItems.controls;
    const subtotal = lineItems.reduce((sum, item) =>
      sum + (+item.get('total_amount')?.value || 0), 0
    );

    const roundedSubtotal = +subtotal.toFixed(2);

    const discount = +this.generateInvoiceForm.get('discount_amount')?.value || 0;
    const tax1 = +this.generateInvoiceForm.get('tax_1')?.value || 0;
    const tax2 = +this.generateInvoiceForm.get('tax_2')?.value || 0;

    const roundedDiscount = +discount.toFixed(2);
    const roundedTax1 = +tax1.toFixed(2);
    const roundedTax2 = +tax2.toFixed(2);

    const net = roundedSubtotal - roundedDiscount;
    const tax1Amt = +(net * roundedTax1 / 100).toFixed(2);
    const tax2Amt = +(net * roundedTax2 / 100).toFixed(2);
    let grandTotal = +(net + tax1Amt + tax2Amt).toFixed(2);

    if (grandTotal < 0) {
      grandTotal = 0;
    }

    this.generateInvoiceForm.get('grand_total')?.setValue(grandTotal, { emitEvent: false });
    this.generateInvoiceForm.get('sub_total')?.setValue(roundedSubtotal, { emitEvent: false });
    this.generateInvoiceForm.get('discount_amount')?.setValue(roundedDiscount, { emitEvent: false });
    this.generateInvoiceForm.get('tax_1')?.setValue(roundedTax1, { emitEvent: false });
    this.generateInvoiceForm.get('tax_2')?.setValue(roundedTax2, { emitEvent: false });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  roundDecimalsBeforeSave(): void {
    this.invoiceLineItems.controls.forEach((control) => {
      const rate = +control.get('rate')?.value || 0;
      const quantity = +control.get('quantity')?.value || 0;
      const total = +control.get('total_amount')?.value || 0;

      control.get('rate')?.setValue(+rate.toFixed(2));
      control.get('quantity')?.setValue(+quantity.toFixed(2));
      control.get('total_amount')?.setValue(+total.toFixed(2));
    });

    const discount = +this.generateInvoiceForm.get('discount_amount')?.value || 0;
    const tax1 = +this.generateInvoiceForm.get('tax_1')?.value || 0;
    const tax2 = +this.generateInvoiceForm.get('tax_2')?.value || 0;

    this.generateInvoiceForm.get('discount_amount')?.setValue(+discount.toFixed(2));
    this.generateInvoiceForm.get('tax_1')?.setValue(+tax1.toFixed(2));
    this.generateInvoiceForm.get('tax_2')?.setValue(+tax2.toFixed(2));
  }

  onSave(): void {
    if (this.generateInvoiceForm.valid && this.invoiceLineItems.length > 0) {
      this.roundDecimalsBeforeSave();
      // Enable 'total' fields temporarily to include their values
      this.invoiceLineItems.controls.forEach(p => p.get('total_amount')?.enable({ emitEvent: false }));


      const invoiceData: IInvoice = this.generateInvoiceForm.getRawValue();

      const invoice_date = this.datePipe.transform(invoiceData.invoice_date, Constants.DATE_FORMAT_FOR_PYTHON);
      invoiceData.invoice_date = invoice_date ? invoice_date : null;

      const sub_start_date = this.datePipe.transform(invoiceData.start_date, Constants.DATE_FORMAT_FOR_PYTHON);
      invoiceData.start_date = sub_start_date ? sub_start_date : null;

      const sub_end_date = this.datePipe.transform(invoiceData.end_date, Constants.DATE_FORMAT_FOR_PYTHON);
      invoiceData.end_date = sub_end_date ? sub_end_date : null;

      // Disable 'total' fields again
      this.invoiceLineItems.controls.forEach(p => p.get('total_amount')?.disable({ emitEvent: false }));

      this.commonService.showSpinner = true;
      if (this.dialog_data.is_edit) {
        this.businessService.updateCustomInvoice(invoiceData).subscribe({
          next: (response: IInvoice) => {
            console.log(response);
            this.dialogRef.close(response);
            this.commonService.showSpinner = false;
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
            this.commonService.showSpinner = false;
          }
        })
      }
      else {
        this.businessService.generateCustomInvoice(invoiceData).subscribe({
          next: (response: IInvoice) => {
            console.log(response);
            this.dialogRef.close(response);
            this.commonService.showSpinner = false;
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
            this.commonService.showSpinner = false;
          }
        })
      }

    }
    else {
      Object.keys(this.generateInvoiceForm.controls).forEach(key => {
        const control = this.generateInvoiceForm.controls[key];

        if (control instanceof FormArray) {
          // Iterate over each FormGroup inside the FormArray
          control.controls.forEach(group => {
            if (group instanceof FormGroup) {
              Object.keys(group.controls).forEach(innerKey => {
                group.controls[innerKey].markAsTouched();
                group.controls[innerKey].updateValueAndValidity({ onlySelf: true, emitEvent: true });
              });
            }
          });
        } else {
          control.markAsTouched();
          control.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        }
      });

      this.generateInvoiceForm.updateValueAndValidity();
      if(this.invoiceLineItems.length<=0){
        this.snackbarService.open(Constants.NOTIFY_MESSAGES.emptyLineItem);
      }
      else{
        this.snackbarService.open(Constants.NOTIFY_MESSAGES.requiredFields);
      }
    }
  }
}
